import React from "react";
import { useTranslation } from "react-i18next";
import {
  Zap,
  Battery,
  TrendingUp,
  Bolt
} from "lucide-react";

export default function ReactiveEnergySummaryCards({ data = {} }) {
  const { t } = useTranslation();

  const formatScaledEnergy = (rawValue, baseUnit = "kWh") => {
    const prefixes = ["k", "M", "G", "T", "P"];
    const normalized = baseUnit.startsWith("k") ? baseUnit.slice(1) : baseUnit;

    let value = Number(rawValue ?? 0);
    let idx = 0;

    while (Math.abs(value) >= 1000 && idx < prefixes.length - 1) {
      value /= 1000;
      idx += 1;
    }

    return {
      valueText: Number.isFinite(value) ? value.toFixed(3) : "0.000",
      unitText: `${prefixes[idx]}${normalized}`,
    };
  };

  const items = [
    {
      label: t('energies.inductiveEnergyTotal'),
      key: "EL_SUM_END",
      unit: "kVArh",
      icon: <Zap size={26} className="text-blue-600" />,
      gradient: "from-blue-500 to-blue-700",
      divide: 1,
    },
    {
      label: t('energies.capacitiveEnergyTotal'),
      key: "EL_SUM_CAP",
      unit: "kVArh",
      icon: <Battery size={26} className="text-red-600" />,
      gradient: "from-red-500 to-red-700",
      divide: 1,
    },
    {
      label: t('energies.apparentEnergyTotal'),
      key: "EL_SUM_VA",
      unit: "kVAh",
      icon: <TrendingUp size={26} className="text-indigo-600" />,
      gradient: "from-indigo-500 to-indigo-700",
      divide: 1,
    },
  ];

  // Aktif enerji diğerlerinden farklı → Import & Export ikili gösterilecek
  const activeImport = formatScaledEnergy(data.EL_SUM_IMPORT, "kWh");
  const activeExport = formatScaledEnergy(data.EL_SUM_EXPORT, "kWh");

  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-6">

      {/* Dinamik Kartlar */}
      {items.map((item) => {
        const scaled = formatScaledEnergy(
          Number(data[item.key] ?? 0) / item.divide,
          item.unit
        );

        return (
          <div
            key={item.label}
            className="bg-white p-5 rounded-xl border border-slate-200 shadow-md hover:shadow-lg transition-shadow"
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-gray-100 p-2 rounded-lg">
                {item.icon}
              </div>
              <span className="text-md font-semibold text-slate-700">
                {item.label}
              </span>
            </div>

            {/* Value */}
            <div className="text-3xl font-bold text-gray-800">
              {scaled.valueText}
            </div>

            <div className="text-xs text-gray-500 mt-1">{scaled.unitText}</div>

            <div
              className={`h-1 w-full mt-5 rounded-full bg-gradient-to-r ${item.gradient}`}
            />
          </div>
        );
      })}

      {/* Aktif Enerji Kartı (Import / Export çiftli gösterim) */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-md hover:shadow-lg transition-shadow">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-gray-100 p-2 rounded-lg">
            <Bolt size={26} className="text-emerald-600" />
          </div>
          <span className="text-md font-semibold text-slate-700">
            {t('energies.activeEnergyImportExport')}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <span className="text-xs text-gray-500">{t('energies.import')}</span>
            <div className="text-xl font-bold text-gray-800">
              {activeImport.valueText}
            </div>
            <span className="text-xs text-gray-400">{activeImport.unitText}</span>
          </div>

          <div>
            <span className="text-xs text-gray-500">{t('energies.export')}</span>
            <div className="text-xl font-bold text-gray-800">
              {activeExport.valueText}
            </div>
            <span className="text-xs text-gray-400">{activeExport.unitText}</span>
          </div>
        </div>

        <div className="h-1 w-full mt-5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-700" />
      </div>

    </div>
  );
}
